import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { showreelVideoUrl } from "../../data/portfolio";

// Cache-busted so a poster swap propagates in the preview environment.
const POSTER_SRC = "/media/showreel-poster.jpg?v=2";

export const Showreel = () => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlay = () => {
    const el = videoRef.current;
    if (!el) return;
    el.controls = true;
    el.muted = false;
    const p = el.play();
    if (p && typeof p.then === "function") {
      p.catch(() => {});
    }
    setIsPlaying(true);
  };

  return (
    <section
      id="showreel"
      data-testid="showreel-section"
      className="relative py-32 sm:py-40 lg:py-56 px-6 sm:px-10 lg:px-12 border-t border-white/10"
    >
      {/* Minimal section label — subtle, exhibition-catalogue style */}
      <div className="max-w-[1600px] mx-auto mb-20 sm:mb-24 lg:mb-28">
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
          className="text-[10px] uppercase tracking-[0.42em] text-white/45"
          data-testid="showreel-label"
        >
          <span style={{ color: "var(--accent-amber)" }}>04</span>
          <span className="mx-3 text-white/25">—</span>
          Showreel
        </motion.p>
      </div>

      {/* Video — the hero. ~88% of the content area, generous whitespace above & below */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1.6, ease: [0.19, 1, 0.22, 1] }}
        className="relative mx-auto w-[92%] sm:w-[90%] lg:w-[88%] max-w-[1600px]"
      >
        {/* Very soft ambient glow — barely there */}
        <div
          className="absolute -inset-16 sm:-inset-24 -z-10 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(200,162,91,0.08), transparent 70%)",
          }}
        />

        <div
          className="group relative w-full aspect-video bg-black overflow-hidden cursor-pointer shadow-[0_80px_200px_rgba(0,0,0,0.85)]"
          data-testid="showreel-player"
          onClick={!isPlaying ? handlePlay : undefined}
          role={!isPlaying ? "button" : undefined}
          aria-label={!isPlaying ? "Play showreel" : undefined}
          tabIndex={!isPlaying ? 0 : -1}
          onKeyDown={(e) => {
            if (!isPlaying && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              handlePlay();
            }
          }}
        >
          <video
            ref={videoRef}
            src={`${showreelVideoUrl}#t=0.5`}
            poster={POSTER_SRC}
            preload="none"
            muted
            playsInline
            aria-label="Valeriy Rudenko — Showreel"
            className={`absolute inset-0 w-full h-full object-cover transition-all duration-[1100ms] ease-[cubic-bezier(0.19,1,0.22,1)] ${
              isPlaying ? "" : "brightness-[0.9] group-hover:brightness-[1.03]"
            }`}
            data-testid="showreel-video"
          />

          {!isPlaying && (
            <>
              {/* Whisper-quiet vignette so the play button reads on any frame */}
              <div
                aria-hidden
                className="absolute inset-0 pointer-events-none transition-opacity duration-700"
                style={{
                  background:
                    "radial-gradient(ellipse at center, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.28) 65%, rgba(0,0,0,0.55) 100%)",
                }}
              />

              {/* Elegant circular Play — the only interface element on the frame */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div
                  className="relative w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 rounded-full border border-white/55 backdrop-blur-[3px] bg-black/15 flex items-center justify-center transition-all duration-[750ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-[1.09] group-hover:border-white group-hover:bg-black/25"
                  data-testid="showreel-play-button"
                >
                  <span
                    aria-hidden
                    className="absolute inset-0 rounded-full border border-white/20 opacity-0 group-hover:opacity-100 scale-100 group-hover:scale-[1.28] transition-all duration-[1100ms] ease-out"
                  />
                  <div className="w-0 h-0 border-y-[12px] sm:border-y-[14px] border-y-transparent border-l-[20px] sm:border-l-[24px] border-l-white ml-1.5 transition-transform duration-500 group-hover:scale-[1.05]" />
                </div>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </section>
  );
};
